import React, { useState, useEffect, useCallback } from 'react';
import { FaTrash, FaSearch, FaFilter, FaDesktop, FaMobileAlt, FaTabletAlt, FaCheckCircle, FaExclamationCircle, FaUserSecret, FaHistory } from 'react-icons/fa';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const LoginHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [toast, setToast] = useState(null);

  const API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API}/api/auth/login-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load login history');
      const data = await res.json();
      setHistory(data.history || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleDelete = async (id) => {
    if (!window.confirm('Move this login record to Recycle Bin?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API}/api/auth/login-history/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Delete failed');
      showToast('Record moved to Recycle Bin');
      setHistory(prev => prev.filter(h => h._id !== id));
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Move ${selectedIds.length} records to Recycle Bin?`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API}/api/auth/login-history/bulk-delete`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ ids: selectedIds })
      });
      if (!res.ok) throw new Error('Bulk delete failed');
      showToast(`${selectedIds.length} records moved to Recycle Bin`);
      setSelectedIds([]);
      fetchHistory();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const filtered = history.filter(item => {
    const matchesSearch = item.userName?.toLowerCase().includes(search.toLowerCase()) ||
      item.userEmail?.toLowerCase().includes(search.toLowerCase()) ||
      item.ip?.toLowerCase().includes(search.toLowerCase()) ||
      item.os?.toLowerCase().includes(search.toLowerCase()) ||
      item.browser?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getDeviceIcon = (device) => {
    if (device === 'Mobile') return <FaMobileAlt className="text-gray-400" />;
    if (device === 'Tablet') return <FaTabletAlt className="text-gray-400" />;
    return <FaDesktop className="text-gray-400" />;
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filtered.map(i => i._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  return (
    <div className="space-y-6 container mx-auto px-4 py-8">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 px-6 py-3 rounded-2xl shadow-2xl text-white font-bold text-sm animate-in fade-in slide-in-from-top-4
          ${toast.type === 'success' ? 'bg-emerald-500 shadow-emerald-500/20' : 'bg-rose-500 shadow-rose-500/20'}`}>
          <div className="flex items-center gap-3">
            {toast.type === 'success' ? <FaCheckCircle className="w-5 h-5" /> : <FaExclamationCircle className="w-5 h-5" />}
            {toast.msg}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-indigo-100 dark:bg-indigo-900/40 shadow-sm">
            <FaHistory className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">Login History</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mt-0.5">
              Monitor user access and administrative impersonations
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center px-4 py-2 rounded-xl bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 text-sm font-bold border border-gray-200 dark:border-dark-600 shadow-sm">
            {history.length} Total Records
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1 group">
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
          <input
            type="text"
            placeholder="Search by name, email, IP, OS or browser..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-white dark:bg-dark-800 border-2 border-gray-100 dark:border-dark-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-medium"
          />
        </div>
        {/* Status Filter */}
        <div className="relative min-w-[180px]">
          <FaFilter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="w-full pl-12 pr-10 py-3.5 rounded-2xl bg-white dark:bg-dark-800 border-2 border-gray-100 dark:border-dark-700 text-sm font-bold text-gray-700 dark:text-gray-300 appearance-none cursor-pointer focus:outline-none focus:border-indigo-500 transition-all shadow-sm"
          >
            <option value="all">All Status</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between p-4 bg-indigo-50 dark:bg-indigo-900/10 border-2 border-indigo-100 dark:border-indigo-900/30 rounded-2xl animate-in fade-in slide-in-from-left-4 duration-300 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-black shadow-lg shadow-indigo-600/20">
              {selectedIds.length}
            </div>
            <span className="text-sm font-bold text-indigo-700 dark:text-indigo-400 capitalize">
              Records selected
            </span>
          </div>
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-rose-500 text-white hover:bg-rose-600 text-sm font-black transition-all shadow-lg shadow-rose-500/20 active:scale-95"
          >
            <FaTrash className="w-4 h-4" />
            Move to Recycle Bin
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-dark-800 border border-gray-100 dark:border-dark-700 rounded-3xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 space-y-4">
            <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            <p className="text-sm font-bold text-gray-500 dark:text-gray-400">Loading history...</p>
          </div>
        ) : error ? (
          <div className="py-24 text-center">
            <div className="w-16 h-16 bg-rose-100 dark:bg-rose-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaExclamationCircle className="w-8 h-8 text-rose-500" />
            </div>
            <p className="text-gray-900 dark:text-white font-bold mb-4">{error}</p>
            <button onClick={fetchHistory} className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-sm shadow-xl shadow-indigo-600/20 hover:bg-indigo-700 transition-all">Try Again</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-32 text-center">
            <div className="w-20 h-20 bg-gray-50 dark:bg-dark-700/50 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaHistory className="w-10 h-10 text-gray-200 dark:text-gray-600" />
            </div>
            <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">No Records Found</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm max-w-xs mx-auto font-medium">
              We couldn't find any login records matching your criteria.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50/50 dark:bg-dark-700/50 border-b border-gray-100 dark:border-dark-700">
                  <th className="px-6 py-5 w-10">
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && selectedIds.length === filtered.length}
                      onChange={handleSelectAll}
                      className="w-5 h-5 text-indigo-600 border-gray-300 rounded-lg focus:ring-indigo-500 dark:focus:ring-offset-dark-800 transition-all cursor-pointer"
                    />
                  </th>
                  <th className="px-4 py-5 font-black text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">User</th>
                  <th className="px-4 py-5 font-black text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Environment</th>
                  <th className="px-4 py-5 font-black text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">IP Address</th>
                  <th className="px-4 py-5 font-black text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Login Time</th>
                  <th className="px-4 py-5 font-black text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                  <th className="px-6 py-5 font-black text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-dark-700/50">
                {filtered.map((item) => {
                  const isSelected = selectedIds.includes(item._id);
                  return (
                    <tr key={item._id} className={`${isSelected ? 'bg-indigo-50/30 dark:bg-indigo-900/10' : 'hover:bg-gray-50/50 dark:hover:bg-dark-700/30'} transition-all cursor-default group`}>
                      <td className="px-6 py-5">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectOne(item._id)}
                          className="w-5 h-5 text-indigo-600 border-gray-300 rounded-lg focus:ring-indigo-500 transition-all cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center font-black text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/30 shadow-sm relative">
                            {item.userName?.[0]?.toUpperCase()}
                            {item.isImpersonated && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center border-2 border-white dark:border-dark-800" title="Admin Impersonation">
                                <FaUserSecret className="w-2.5 h-2.5 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm font-black text-gray-900 dark:text-white flex items-center gap-2">
                              {item.userName}
                              {item.isImpersonated && <span className="px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 text-[10px] font-black tracking-tight">IMPERSONATED</span>}
                            </span>
                            <span className="text-xs font-bold text-gray-500 dark:text-gray-400">{item.userEmail}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-5">
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-2 text-xs font-bold text-gray-700 dark:text-gray-300">
                            {getDeviceIcon(item.device)}
                            {item.os}
                          </div>
                          <div className="text-[10px] font-black text-gray-400 uppercase tracking-wide">
                            Browser: {item.browser}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-5">
                        <span className="text-sm font-mono font-bold text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-dark-700 px-2 py-1 rounded-lg">
                          {item.ip || '0.0.0.0'}
                        </span>
                      </td>
                      <td className="px-4 py-5 text-sm font-bold text-gray-600 dark:text-gray-400">
                        {formatDate(item.loginTime)}
                      </td>
                      <td className="px-4 py-5">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black tracking-tight ${
                          item.status === 'success' 
                            ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' 
                            : 'bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400'
                        }`}>
                          {item.status === 'success' ? <FaCheckCircle className="w-3 h-3" /> : <FaExclamationCircle className="w-3 h-3" />}
                          {item.status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-right">
                        <button
                          onClick={() => handleDelete(item._id)}
                          className="p-2.5 rounded-xl bg-gray-50 dark:bg-dark-700 text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-all opacity-0 group-hover:opacity-100"
                          title="Delete"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginHistory;
